import { AdditionalFundsPurpose, PropertyType } from '@/helpers/const';
import type { AdditionalFundsRequiredEntry, PropertyTypeEnum } from '@/types';
import type { AdditionalFundsRequiredProperty } from '@/types/AdditionalFundsRequiredProperty';

/**
 * The single-security loan purposes that collect a property being purchased, and the type that
 * property gets. A Map rather than an object so an unrecognised purpose can never match an
 * inherited key such as "toString".
 */
const PURCHASE_PROPERTY_TYPE_BY_PURPOSE: ReadonlyMap<string, PropertyTypeEnum> = new Map([
  [AdditionalFundsPurpose.PURCHASE_VACANT_LAND, PropertyType.VACANT_LAND],
  [
    AdditionalFundsPurpose.PURCHASE_RETIREMENT_LAND_LEASE_OR_COMMUNITY_HOME,
    PropertyType.RETIREMENT_LAND_OR_COMMUNITY_HOME,
  ],
]);

/**
 * Everything the loan-purpose form only shows for a purchase purpose: the nested address the
 * broker edits, the flattened copy of it the API mapper reads, and the purchase answers.
 * Typed loosely because the prefilled form state also carries address-option bookkeeping
 * (`id`, `index`) that no type declares.
 */
const PURCHASE_PROPERTY_KEYS: ReadonlySet<string> = new Set([
  'propertyType',
  'propertyPurpose',
  'propertyAddress',
  'isThereContractForSale',
  'settlementDate',
  'purchasePrice',
  'depositAmount',
  'depositType',
  'stampDutyStatus',
  'transferFeeCovered',
  'addressLine',
  'addressLine2',
  'locality',
  'state',
  'postcode',
  'countryCode',
  'addressData',
  'addressManuallyCreated',
  'id',
  'index',
]);

export const isPurchaseFundsPurpose = (reasonForFundsOption?: string): boolean =>
  reasonForFundsOption !== undefined && PURCHASE_PROPERTY_TYPE_BY_PURPOSE.has(reasonForFundsOption);

/**
 * Builds the single-security purchase property from the loan-purpose form values.
 *
 * The form keeps every answer in its state, including the ones a scenario prefilled and the ones
 * entered before the purpose was changed; switching purpose only hides the purchase inputs. So the
 * purchase property is only kept for a purchase purpose, with its type derived from that purpose.
 * For any other purpose the hidden purchase answers are dropped and only the fields the broker can
 * see (the purpose itself, funds required, contribution, description) are carried — otherwise a
 * half-entered or prefilled property ships with the application and fails submission on a blank
 * postcode the broker has no field to fix (JS-10505).
 */
export const buildAdditionalFundsRequiredProperties = (
  values: AdditionalFundsRequiredEntry,
): AdditionalFundsRequiredProperty => {
  const propertyType = PURCHASE_PROPERTY_TYPE_BY_PURPOSE.get(values.reasonForFundsOption);
  if (propertyType) {
    return { ...values, ...values.propertyAddress, propertyType } as AdditionalFundsRequiredProperty;
  }

  return Object.fromEntries(
    Object.entries(values).filter(([key]) => !PURCHASE_PROPERTY_KEYS.has(key)),
  ) as unknown as AdditionalFundsRequiredProperty;
};
